import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { NavbarLinks } from "../../utils/navbarLinks";
import { Link, useNavigate } from "react-router-dom";
import SearchComponent from "./navbar/searchComponent";
import { Hidden, useMediaQuery, useTheme } from "@mui/material";
import logoImg from "../../images/MEF_Logo_PDC_01_small.jpg"

const ResponsiveNavbar = () => {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const mdScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Verify NavbarLinks is available and is an array
  const validNavbarLinks = React.useMemo(() => {
    return Array.isArray(NavbarLinks) ? NavbarLinks : [];
  }, []);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const renderGroupedCaseStudies = (subLinks) => {
    // Group the case studies
    const groupedStudies = subLinks.reduce((acc, study) => {
      if (!acc[study.group]) {
        acc[study.group] = [];
      }
      acc[study.group].push(study);
      return acc;
    }, {});
    return (
      <Box sx={{ display: 'flex', gap: 4 }}>
        {Object.entries(groupedStudies).map(([groupName, studies]) => (
          <Box key={groupName}>
            <Typography
              color="primary"
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 1 }}
            >
              {groupName}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {studies.map((study, index) => (
                <Link
                  key={index}
                  to={study.link}
                  style={{ textDecoration: 'none' }}
                >
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    fontWeight={600}
                    marginLeft="4px"
                    sx={{
                      textDecoration: "underline",
                    }}
                  >
                    {study.name}
                  </Typography>
                </Link>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const handleNavigation = (navLinkInfo) => {
    if (navLinkInfo?.subLinks?.[0]?.link) {
      navigate(navLinkInfo.subLinks[0].link);
      handleCloseNavMenu();
    }
  };

  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#f5f5f9",
      color: theme.palette.primary.main,
      maxWidth: "max-content",
      padding: "12px",
      border: "1px solid #dadde9",
      zIndex: 2,
      position: "relative",
      top: -10,
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.primary.main,
    },
  }));

  // Early return if NavbarLinks is not properly loaded
  if (!validNavbarLinks.length) {
    console.log('No valid navbar links found'); // Debug log
    return null; // Or a loading state
  }

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "#f9f9f9",
        boxShadow: "0px 2px 3px -1px rgb(84 77 160 / 20%)",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "left",
          paddingLeft: 2.5,
          paddingRight: 2.5,
        }}
      >
        {/* Logo and Title Section */}
        <Link to="/">
          <Box textAlign="left" display="flex" alignItems="center">
            <img
              src={logoImg}
              alt="logo"
              width={mdScreen ? 65 : 85}
              height={mdScreen ? 30 : 50}
            />
            <Typography
              variant="subtitle2"
              noWrap
              ml={1}
              mt={0.5}
              sx={{
                display: { xs: "none", lg: "flex" },
                color: "primary.main",
                textDecoration: "none",
                lineHeight: "1.25em",
                fontWeight: 600,
              }}
            >
              Identify & Quantify <br /> Macro-Energy-Esg Risk
            </Typography>
          </Box>
        </Link>

        {/* Mobile Menu Section */}
        <Box sx={{ display: { xs: "flex", md: "none" } }}>
          <Hidden lgUp>
            <SearchComponent />
          </Hidden>
          <IconButton
            size="large"
            aria-label="navigation menu"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleOpenNavMenu}
            color="inherit"
          >
            <MenuIcon color="primary" />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorElNav}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{
              display: { xs: "block", md: "none" },
            }}
          >
            {validNavbarLinks.map((navLinkInfo, index) => (
              <MenuItem
                key={index}
                onClick={() => handleNavigation(navLinkInfo)}
              >
                <Typography
                  color="text.secondary"
                  variant="body1"
                  fontWeight={600}
                  sx={{
                    textDecoration: "underline",
                    fontSize: "0.9375rem",
                  }}
                >
                  {navLinkInfo.title}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Desktop Menu Section */}
        <Box
          sx={{ display: { xs: "none", md: "flex" } }}
          gap={{ md: 0.1, lg: 0.3 }}
        >
          {validNavbarLinks.map((navLinkInfo, index) => {
            // Ensure subLinks is an array before mapping
            const subLinks = Array.isArray(navLinkInfo.subLinks) ? navLinkInfo.subLinks : [];

            return (
              <HtmlTooltip
                key={index}
                title={
                  <React.Fragment>
                    {navLinkInfo.title === "Case Studies" ? (
                      renderGroupedCaseStudies(subLinks)
                    ) : (
                      <Box display="flex" gap={4}>
                        {subLinks.map((subInfo, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subInfo?.link || "#"}
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography
                              color="text.secondary"
                              variant="body1"
                              fontWeight={600}
                              sx={{
                                textDecoration: "underline",
                              }}
                            >
                              {subInfo?.name}
                            </Typography>
                          </Link>
                        ))}
                      </Box>
                    )}
                  </React.Fragment>
                }
                arrow
              >
                <Button
                  type="button"
                  onClick={() => handleNavigation(navLinkInfo)}
                  sx={{
                    color: "text.secondary",
                    display: "flex",
                    textTransform: "uppercase",
                    fontSize: { md: "0.8125rem", lg: "0.9375rem" },
                    fontWeight: "bold",
                    padding: { md: "4px 8px", lg: "6px 8px" },
                  }}
                >
                  {navLinkInfo.title}
                </Button>
              </HtmlTooltip>
            );
          })}
        </Box>

        {/* Desktop Search Section */}
        <Hidden mdDown>
          <SearchComponent />
        </Hidden>
      </Toolbar>
    </AppBar>
  );
};

export default ResponsiveNavbar;
